/**
 * Ticket VS2: Aussendung an einen Empfängerkreis – reine Logik.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * VERMERK – ZWEI ZUSTÄNDE, NICHT EINER
 *
 * Eine Zeile des Empfängerkreises kennt „vorgemerkt" und „eingereiht".
 *
 *  - `vorgemerkt` schützt nur gegen einen parallelen Zugriff. Sie nimmt die
 *    Adresse NICHT aus künftigen Läufen.
 *  - `eingereiht` (technisch: gesetztes `enqueued_at`) ist der Zustand, der die
 *    Adresse endgültig aus künftigen Läufen nimmt.
 *
 * Warum getrennt: Ein Abbruch zwischen beidem darf keinen der zwei Fehler
 * erzeugen. Würde schon das Vormerken die Adresse abschließen, wäre die Person
 * „angeschrieben", ohne je eine Nachricht zu erhalten – bei einem Abbruch
 * still verloren. Umgekehrt ist das
 * erneute Einreihen gefahrlos, weil die Versandkennung je Aussendung und
 * Kontakt immer dieselbe ist (`broadcastIdempotencyKey`): Der Versanddienst
 * erkennt sie wieder und liefert nicht zweimal aus.
 *
 * Daraus folgt die Auswahlregel des nächsten Laufs: offen ist jede Zeile ohne
 * `enqueued_at` – gleichgültig, ob sie vorgemerkt ist.
 * ═══════════════════════════════════════════════════════════════════════════
 */

export const BROADCAST_STATES = ["offen", "vorgemerkt", "eingereiht", "fehlgeschlagen"] as const;
export type BroadcastRecipientState = (typeof BROADCAST_STATES)[number];

export const BROADCAST_STATUSES = ["entwurf", "laeuft", "angehalten", "abgeschlossen"] as const;
export type BroadcastStatus = (typeof BROADCAST_STATUSES)[number];

export const BROADCAST_STATUS_LABELS: Record<BroadcastStatus, string> = {
  entwurf: "Entwurf",
  laeuft: "Läuft",
  angehalten: "Angehalten",
  abgeschlossen: "Abgeschlossen",
};

/** Vorbelegte Obergrenze je Lauf. */
export const BROADCAST_DEFAULT_RUN_LIMIT = 50;

/** Höchstwert, den die Oberfläche je Lauf zulässt. */
export const BROADCAST_MAX_RUN_LIMIT = 500;

export const BROADCAST_PROCESSING_STATES = [
  "idle",
  "running",
  "waiting_limit",
  "paused",
  "completed",
  "failed",
] as const;
export type BroadcastProcessingState = (typeof BROADCAST_PROCESSING_STATES)[number];

export const BROADCAST_PROCESSING_LABELS: Record<BroadcastProcessingState, string> = {
  idle: "Bereit",
  running: "Wird automatisch weiterverarbeitet",
  waiting_limit: "Wartet auf freie Versandkapazität",
  paused: "Wegen Rückläufern angehalten",
  completed: "Abgeschlossen",
  failed: "Verarbeitung fehlgeschlagen",
};

/** Rückläuferquote, ab der die Aussendung sich selbst anhält. */
export const BROADCAST_BOUNCE_THRESHOLD = 0.02;

/**
 * Erst ab dieser Zahl eingereihter Adressen wird die Quote gerechnet. Ohne
 * diese Grundmenge hielte ein einzelner Rückläufer unter den ersten drei
 * Adressen den Lauf an (33 Prozent).
 */
export const BROADCAST_BOUNCE_MIN_BASE = 20;

/**
 * Notbremse: so viele neue Rückläufer im laufenden Fenster halten immer an,
 * unabhängig von Quote und Grundmenge.
 */
export const BROADCAST_BOUNCE_HARD_STOP = 10;

/** Nur eigene Adressen dürfen Testempfänger sein. */
export const BROADCAST_TEST_DOMAIN = "creategy.de";

export function isOwnAddress(email: string): boolean {
  const value = email.trim().toLowerCase();
  return /^[^@\s]+@creategy\.de$/.test(value);
}

/** Vorlagenname der Aussendung – der Anfang macht sie werblich (Ticket VS0). */
export function broadcastTemplateName(broadcastId: string): string {
  return `aussendung-${broadcastId}`;
}

/**
 * Versandkennung je Adresse. Immer dieselbe, damit ein erneutes Einreihen nach
 * einem Abbruch nicht zweimal zustellt.
 */
export function broadcastIdempotencyKey(broadcastId: string, recipientRowId: string): string {
  return `broadcast-${broadcastId}-${recipientRowId}`;
}

/** Eine Zeile ist offen, solange sie nicht eingereiht ist. */
export function isOpenRecipient(row: { enqueuedAt: string | null }): boolean {
  return row.enqueuedAt === null;
}

/** Wie viele Adressen darf dieser Lauf nehmen? */
export function runAllowance(open: number, limit: number): number {
  const cap = Number.isFinite(limit) && limit > 0 ? Math.floor(limit) : 0;
  return Math.max(0, Math.min(Math.max(0, Math.floor(open)), cap));
}

export function bounceQuote(enqueued: number, bounced: number): number {
  if (!Number.isFinite(enqueued) || enqueued <= 0) return 0;
  return bounced / enqueued;
}

/**
 * Hält die Aussendung an? Ab `BROADCAST_BOUNCE_MIN_BASE` eingereihten Adressen
 * und einer Quote von mindestens zwei Prozent.
 */
export function shouldPauseForBounces(enqueued: number, bounced: number): boolean {
  if (enqueued < BROADCAST_BOUNCE_MIN_BASE) return false;
  return bounceQuote(enqueued, bounced) >= BROADCAST_BOUNCE_THRESHOLD;
}

/** Beim Fortsetzen quittierter Altstand. */
export type BounceBaseline = { enqueued: number; bounced: number };

export type BounceWindow = {
  /** Eingereihte Adressen seit dem Fortsetzen. */
  enqueued: number;
  /** Neue harte Rückläufer seit dem Fortsetzen. */
  bounced: number;
  /** Gesamtstand über die Lebensdauer. */
  totalEnqueued: number;
  totalBounced: number;
  pause: boolean;
  /** Grund des Anhaltens: Quote oder Notbremse. */
  cause: "quote" | "hard-stop" | null;
};

/**
 * Rechnet die Rückläuferquote auf dem Fenster seit dem Fortsetzen.
 *
 * Ohne Fenster hielte ein einmal erreichter Altstand die Aussendung endlos an:
 * die Quote über die gesamte Lebensdauer bleibt über der Schwelle, egal wie
 * saubere die neu eingereihten Adressen sind.
 */
export function bounceWindow(
  enqueued: number,
  bounced: number,
  baseline: BounceBaseline = { enqueued: 0, bounced: 0 },
): BounceWindow {
  const totalEnqueued = Math.max(0, Math.floor(enqueued));
  const totalBounced = Math.max(0, Math.floor(bounced));
  const windowEnqueued = Math.max(0, totalEnqueued - Math.max(0, Math.floor(baseline.enqueued)));
  const windowBounced = Math.max(0, totalBounced - Math.max(0, Math.floor(baseline.bounced)));

  const hardStop = windowBounced >= BROADCAST_BOUNCE_HARD_STOP;
  const quote = shouldPauseForBounces(windowEnqueued, windowBounced);
  return {
    enqueued: windowEnqueued,
    bounced: windowBounced,
    totalEnqueued,
    totalBounced,
    pause: hardStop || quote,
    cause: hardStop ? "hard-stop" : quote ? "quote" : null,
  };
}

/** Der angehaltene Zustand blockiert jede Eintrittsstelle. */
export function isPaused(status: string): boolean {
  return status === "angehalten";
}

/** Darf ein Lauf beginnen oder fortgesetzt werden? */
export function mayRun(status: string): { ok: boolean; reason?: string } {
  if (isPaused(status)) return { ok: false, reason: "Die Aussendung ist angehalten." };
  if (status === "abgeschlossen") return { ok: false, reason: "Die Aussendung ist abgeschlossen." };
  if (!BROADCAST_STATUSES.includes(status as BroadcastStatus)) {
    return { ok: false, reason: "Unbekannter Zustand." };
  }
  return { ok: true };
}

export function describeBounceQuote(enqueued: number, bounced: number): string {
  if (enqueued <= 0) return "keine Zustellversuche";
  const pct = (bounceQuote(enqueued, bounced) * 100).toFixed(1).replace(".", ",");
  return `${pct} % (${bounced} von ${enqueued})`;
}

/** Anhaltegrund im Klartext: beide Quoten und die Ursache. */
export function describeBounceWindow(win: BounceWindow): string {
  const gesamt = describeBounceQuote(win.totalEnqueued, win.totalBounced);
  const fenster = describeBounceQuote(win.enqueued, win.bounced);
  const grund =
    win.cause === "hard-stop"
      ? `Notbremse: ${win.bounced} neue Rückläufer seit dem Fortsetzen`
      : `Schwelle zwei Prozent seit dem Fortsetzen erreicht`;
  return `${grund}. Seit dem Fortsetzen ${fenster}, gesamt ${gesamt}.`;
}

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * VERMERK – EINGEREIHT IST NICHT ZUGESTELLT
 *
 * `eingereiht` sagt nur: die Nachricht liegt in der Warteschlange. Ob sie
 * hinausgegangen ist, steht allein in der Versandspur (`email_send_log`).
 * `recipientDeliveryState` ist die einzige Stelle, an der beide Angaben zu
 * einem Zustand zusammengeführt werden – die Ansicht leitet nichts nach.
 * ═══════════════════════════════════════════════════════════════════════════
 */
export const RECIPIENT_VIEW_STATES = [
  "zugestellt",
  "ruecklaeufer",
  "unterdrueckt",
  "abgemeldet",
  "unterwegs",
  "fehlgeschlagen",
  "offen",
] as const;
export type RecipientViewState = (typeof RECIPIENT_VIEW_STATES)[number];

export const RECIPIENT_VIEW_STATE_LABELS: Record<RecipientViewState, string> = {
  zugestellt: "Zugestellt",
  ruecklaeufer: "Rückläufer",
  unterdrueckt: "Unterdrückt",
  abgemeldet: "Abgemeldet",
  unterwegs: "Unterwegs",
  fehlgeschlagen: "Fehlgeschlagen",
  offen: "Offen",
};

export type RecipientStateInput = {
  /** Zustand der Empfängerzeile (`offen`, `vorgemerkt`, …). */
  state: string;
  enqueuedAt: string | null;
  bouncedAt: string | null;
  unsubscribedAt: string | null;
  /** Jüngster Eintrag der Versandspur zu dieser Kennung, falls vorhanden. */
  sendStatus: string | null;
};

/** Zusammenführung von Warteschlange und Versandspur – Reihenfolge zählt. */
export function recipientDeliveryState(row: RecipientStateInput): RecipientViewState {
  if (row.bouncedAt) return "ruecklaeufer";
  if (row.unsubscribedAt) return "abgemeldet";
  switch (row.sendStatus) {
    case "sent":
      return "zugestellt";
    case "bounced":
    case "complained":
    case "dlq":
    case "failed":
      return "ruecklaeufer";
    case "suppressed":
      return "unterdrueckt";
    default:
      break;
  }
  if (row.state === "fehlgeschlagen") return "fehlgeschlagen";
  return row.enqueuedAt ? "unterwegs" : "offen";
}

/** Hinweis auf die zulässigen Platzhalter im Editor. */
export const BROADCAST_PLACEHOLDER_HINT =
  "Platzhalter: {{vorname}}, {{nachname}}, {{organisation}}. Fehlt ein Wert, wird die Lücke sauber geschlossen – aus „Guten Tag {{vorname}} {{nachname}},“ wird dann „Guten Tag,“. Der Abmeldelink wird immer angehängt und ist nicht abschaltbar.";


/**
 * Ticket VS3 (Richtigstellung): Die Datenschnittstelle gibt je Abfrage nur
 * einen Block Zeilen heraus. Wer ohne Seitenlauf liest, verliert bei mehr als
 * einer Blockgröße genau die zuletzt sortierten Zeilen – in der Übersicht der
 * Aussendungen waren das die jüngsten Spurzeilen, weshalb „versendet" und
 * „angekommen" zu niedrig standen. Jede Sammelabfrage läuft deshalb in Seiten.
 */
export const SELECT_PAGE_SIZE = 1000;

export async function selectAllPages<T>(
  page: (
    from: number,
    to: number,
  ) => PromiseLike<{ data: unknown; error: { message?: string } | null }>,
  label: string,
  pageSize: number = SELECT_PAGE_SIZE,
): Promise<T[]> {
  const all: T[] = [];
  for (let from = 0; ; from += pageSize) {
    const { data, error } = await page(from, from + pageSize - 1);
    if (error) throw new Error(error.message ?? label);
    const rows = (data ?? []) as T[];
    all.push(...rows);
    if (rows.length < pageSize) return all;
  }
}
